import CustomHtmlKeyboardResponsePlugin from "../../customPlugin/customHtmlKeyboardResponse";

import { KEY_CODES } from "../../gearshiftGame/constants";
import { Game } from "../../gearshiftGame/game";
import { Score } from "../../gearshiftGame/score";
import { createStimulus, hideElement } from "../../gearshiftGame/utils";
import {
  getGearshiftMissionConfig,
  getLayoutVideoUrl,
} from "../../lib/utils";
import { buildAchievementsTrial } from "../trials/achievements";
import { buildFeedbackTrial } from "../trials/feedback";
import { buildInstructionsTrial } from "../trials/instructions";
import { tutorialToggles } from "../../config/tutorialToggles";
import { updateDebugMissionConfig } from "../ui/debugHelpers";
import { buildTutorialWithPhasesMissionTwo } from "./utils/buildTutorialWithPhasesMissionTwo";
import { addFirstTaskMetadata } from "./utils/missionHelpers";
import {
  buildPhase0,
  buildPhase1,
  buildPhase1_1,
  buildPhase1_2,
  buildPhase2,
  buildPhase3,
  buildPhase4,
  buildPhase5,
  buildPhase7,
} from "./phases/missionTwoPhases";
import { buildSkipMissionProcedure } from "./skipMission";
import {
  MISSION_TWO_PAGE_COUNT,
  buildMissionTwoInstructionPages,
} from "./instructionBuilders/missionTwo/buildMissionTwoInstructionPages";
import { injectSurveyPanel, removeSurveyPanel } from "./hq/buildHQSurveyPanel.js";
import { injectHQMessage, removeHQMessage } from "./hq/buildHQMessage.js";
import { getHQMessagePanelConfig } from "./hq/localHQMessages.js";
/**
 * @type {number}
 * @description The current mission
 */
const MISSION_NUMBER = 2;

/**
 * @type {Score}
 * @description The user's score
 * NOTE: Used here so we don't have to pass it through every single function
 */
let SCORE;

/**
 * Global counter for tutorial failures in this mission
 */
let tutorialFailureCount = 0;

/**
 * Global failure limit for this mission
 */
let failureLimit = 2;

/**
 * Hard-coded truck variables for the truck demos, so they don't depend on the first TASK trial
 * (e.g. a study whose first trial has avoidTruck: 0 would make "speed up to pass the truck" impossible)
 */
const TRUCK_DEMO_OVERRIDES = {
  occurrenceTruck: 1,
  avoidTruck: 1,
  truckSpeed: 2,
};

/**
 * Builds the first mission of the game
 * @param {Object} jsPsych The jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @returns The timeline object for the first mission
 */
export async function buildMissionTwo(jsPsych, metadata) {
  // Reset tutorial failure counter when entering this mission
  tutorialFailureCount = 0;

  let config;
  try {
    config = await getGearshiftMissionConfig(jsPsych, metadata, MISSION_NUMBER);
  } catch (e) {
    console.error(`Unable to load mission config file for mission ${MISSION_NUMBER}`);
  }

  // No config file was given for mission 1, skip this mission (no file given or failed to load the file)
  if (!config) return buildSkipMissionProcedure(MISSION_NUMBER);

  // Config file given - build the mission!
  console.log("Mission Two Configuration:", config);

  // Update debug overlay with mission configuration
  updateDebugMissionConfig(jsPsych, config);

  // Initialize variables shared across the mission
  const {
    missionLives,
    missionMinScore,
    missionMaxCrash,
    missionNumTrials,
    instructionsMaxRetries,
    instructionsAudio,
    trials,
    thisFile,
  } = config;
  // Get the tutorial settings for this mission
  const missionTutorials = {
    ...(tutorialToggles.mission2 ?? {}),
    ...(config.tutorials ?? {}),
    ...(instructionsAudio !== undefined ? { instructionsAudio } : {}),
  };
  // Get the failure limit from the mission config, fallback to tutorial toggles
  failureLimit = instructionsMaxRetries ?? missionTutorials.failsCountLimit ?? 2;
  SCORE = new Score(
    jsPsych,
    missionLives, // # of lives to start the mission with
    metadata.rewardReductionByReset // Amount to reduce score by when the user runs out of lives
  );

  // Build the timeline from the missionConfig trials array
  const missionTwoTimeline = trials.map((trialConfig) => {
    const stimulus = () => createStimulus(MISSION_NUMBER, missionNumTrials, trialConfig);
    switch (trialConfig.trialType) {
      case "INSTRUCTION":
        // NOTE: Instructions trial is the first trial of the mission
        return buildMissionTwoInstructions(jsPsych, trialConfig, metadata, stimulus(), trials, missionTutorials);
      case "TASK":
        return buildMissionTwoTask(jsPsych, metadata, trialConfig, stimulus);
      case "MESSAGE":
        return buildMissionTwoMessage(jsPsych, metadata, trialConfig, stimulus);
      case "FEEDBACK":
        // NOTE: Feedback trial is the last trial of the mission
        return buildMissionTwoFeedback(jsPsych, trialConfig, missionMinScore, missionMaxCrash);
      case "SURVEY":
        return buildMissionTwoSurvey(jsPsych, metadata, trialConfig, stimulus);
      default:
        throw new Error("An unknown trial type was found:", trialConfig.trialType);
    }
  });

  // Add task_metadata to the first TASK trial
  addFirstTaskMetadata(missionTwoTimeline, trials, metadata);

  return {
    data: {
      missionNumber: MISSION_NUMBER,
      missionFile: thisFile,
      missionNumTrials,
      skipped: false,
    },
    save_trial_parameters: { stimulus: false },
    timeline: missionTwoTimeline,
  };
}

/**
 * Builds the trial for displaying the instructions for mission two
 * @param {Object} jsPsych The jsPsych instance being used to run the task
 * @param {Object} trialConfig The configuration object for a specific trial
 * @param {Object} metadata The global metadata of the experiment
 * @param {String} stimulus The HTML stimulus to show
 * @param {Array} allTrials All trials from the mission config (to find a TASK trial for tutorial)
 * @returns
 */
function buildMissionTwoInstructions(jsPsych, trialConfig, metadata, stimulus, allTrials, missionTutorials) {
  // Get audio enabled status from backend config, fallback to local configs
  const instructionAudioEnabled = missionTutorials.instructionsAudio ?? tutorialToggles.mission2?.instructionsAudio ?? true;
  const instructionAudio = (src) => (instructionAudioEnabled ? src : undefined);

  // find the first TASK trial to use for the tutorial
  const tutorialConfig = { ...(allTrials.find((t) => t.trialType === "TASK") || trialConfig) };
  tutorialConfig.trialNumber = 1;
  const tutorialStimulus = createStimulus(MISSION_NUMBER, 1, tutorialConfig);

  const pages = buildMissionTwoInstructionPages(jsPsych, instructionAudio);
  const totalPages = MISSION_TWO_PAGE_COUNT;

  const timeline = [];
  const tutorialEnabled = (key, fallback = true) => missionTutorials[key] ?? fallback;

  if (pages.length > 0) {
    // Pages 1-2
    const firstPages = pages.slice(0, Math.min(2, pages.length));
    timeline.push(
      buildInstructionsTrial(jsPsych, firstPages, {
        total_pages: totalPages,
        page_offset: 0,
      })
    );
  }

  // insert the first tutorial task after the first two pages (phases 0, 1)
  if (pages.length >= 2 && tutorialEnabled("truckTrust")) {
    timeline.push(
      buildMissionTwoTaskWithPhases(
        jsPsych,
        metadata,
        tutorialConfig,
        tutorialStimulus,
        [0, 1],
        {
          agentFile: "footer_LaneLabels_speedDown.svg",
          disableAcceleration: true,
          hideTargetCar: false,
          customTrialConfigOverrides: TRUCK_DEMO_OVERRIDES,
          followUpOnly: true,
          tutorialHeader: {
            title: "Practicing lane decision with truck (1/2)",
            instruction: "Trucks will pass in front of you"
          },
        }
      )
    );
    // second part of truck trusting tutorial with reveal
    timeline.push(
      buildMissionTwoTaskWithPhases(
        jsPsych,
        metadata,
        tutorialConfig,
        tutorialStimulus,
        [0, 1, 2],
        {
          agentFile: "footer_LaneLabels_speedUp_reveal.svg",
          disableDeceleration: true,
          hideTargetCar: false,
          validateSlashKey: true,
          customTrialConfigOverrides: TRUCK_DEMO_OVERRIDES,
          tutorialHeader: {
            title: "Practicing lane decision with truck (2/2)",
            instruction: "Use '__FORWARD_KEY__' to pass the truck, then press '__SPACE_KEY__' near the car to reveal it"
          },
          ready: {
            imageOrVideoUrl: () => getLayoutVideoUrl("instructions/videos/m2/2_pass-truck.mp4"),
          },
          addPhase2Delay: true,
        }
      )
    );
  }

  // push instruction page 3
  if (pages.length > 2) {
    const page3 = pages.slice(2, 3);
    timeline.push(
      buildInstructionsTrial(jsPsych, page3, {
        total_pages: totalPages,
        page_offset: 2,
      })
    );
  }

  // push instruction page 4
  if (pages.length > 3) {
    const page4 = pages.slice(3, 4);
    timeline.push(
      buildInstructionsTrial(jsPsych, page4, {
        total_pages: totalPages,
        page_offset: 3,
      })
    );
  }

  // change to key map demo soon
  // insert the second tutorial task with truck phases after page 3
  if (pages.length >= 4 && tutorialEnabled("fullLane")) {
    timeline.push(
      buildMissionTwoTaskWithPhases(
        jsPsych,
        metadata,
        tutorialConfig,
        tutorialStimulus,
        [0, 1, "1.1", "1.2", 2, 3, 4, 5],
        {
          disableAcceleration: true,
          enableTruckPhaseSkip: true,
          customTrialConfigOverrides: TRUCK_DEMO_OVERRIDES,
          tutorialHeader: {
            title: "Practicing full trial (1/1)",
            instruction: "Choose a lane"
          },
          ready: {
            imageOrVideoUrl: () => getLayoutVideoUrl("instructions/videos/m2/2_truck-interact.mp4"),
          },
        }
      ) // Turn off acelleration for this specific tutorial
    );
  }

  // push the remaining instruction pages
  if (pages.length > 4) {
    const remaining = pages.slice(4);
    timeline.push(
      buildInstructionsTrial(jsPsych, remaining, {
        total_pages: totalPages,
        page_offset: 4,
      })
    );
  }

  return {
    data: { trialConfig },
    timeline,
    on_finish: (data) => SCORE.assignScore(data),
  };
}

/**
 * Builds the trial needed for mission two's task
 * @param {Object} jsPsych The jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @param {Object} missionConfig The task configuration used to build the mission
 * @param {Object} trialConfig The task configuration used to build the trial
 * @param {Object} additionalConfig Additional configuration needed to
 * @returns
 */
// TODO: Need to add data.name to these trials?
function buildMissionTwoTask(jsPsych, metadata, trialConfig, stimulus) {
  const newGame = new Game(jsPsych, metadata, SCORE, trialConfig);

  // Displays the truck while the target car is moving towards the truck
  const truckPhase = {
    timeline: [
      // Phase 1.1: Truck signal is displayed and waits for user to make a decision
      // Phase ends when time runs out or user selects a lane
      {
        type: CustomHtmlKeyboardResponsePlugin,
        choices: ["KeyS", "KeyD", "KeyF", "KeyK", "KeyL", "Semicolon", "Space"],
        on_load: () => newGame.startPhase1_1(),
        on_finish: (data) => {
          data.name = "Phase1A_truckBlockingTarget";
          // Check if user choose a lane, this determines if we run phase 2 and 3
          if ((data.response_event_code ?? data.response) !== "Space") {
            newGame.playEffect("shiftLane");
            newGame.followTruckSignal = 1; // this is used in the game, so we keep it as variable
            data.keyCode =
              KEY_CODES[
              jsPsych.data.get().last(1).values()[0].response_event_code
              ?? jsPsych.data.get().last(1).values()[0].response
              ] ?? null;
            data.driverMovedTruck = 2; //driver did not try to move truck
          } else {
            //case: driver pressed space to get rid of truck
            newGame.followTruckSignal = 0;
            // NGJ adds this code below because we want to see how often people press spacebar
            data.keyCode = 0; //we record spacebar presses as zeros
            if (newGame.truckMoves === 0) {
              //driver could NOT move truck even though they tried to move it
              data.driverMovedTruck = 0;
              newGame.playEffect("lockOn");
            } else {
              //driver could move truck and also tried to move truck
              data.driverMovedTruck = 1;
            }
          }
          data.followTruckSignal = newGame.followTruckSignal; // we write this variable also into the datafile

          SCORE.assignScore(data);
        },
      },
      // Phase 1.2: Handles the truck movement off the screen, either:
      // 1) Moves the truck right and then off the highway (truckMoves === 1)
      // 2) Truck remains in place until the user responds
      // Conditional, occurs when the user doesn't chose a lane in phase 1.1
      // NOTE: After this we move on to phase2 as seen in mission 1
      //if truckMoves===1: specific keys (s, d, f, k, l, ;) are enabled for the participant to respond with.
      {
        timeline: [
          {
            type: CustomHtmlKeyboardResponsePlugin,
            choices: () =>
              newGame.truckMoves === 1
                ? "NO_KEYS"
                : ["KeyS", "KeyD", "KeyF", "KeyK", "KeyL", "Semicolon"],
            trial_duration: () => (newGame.truckMoves === 1 ? 4000 : null),
            on_load: () => newGame.startPhase1_2(),
            on_finish: (data) => {
              data.name = "Phase1B_truckBlockingTarget";
              if (data.response !== undefined) {
                const key = data.response_event_code ?? data.response;
                data.keyCode = KEY_CODES[key] ?? null;
              }
              if ((data.response_event_code ?? data.response) === "Space") {
                data.keyCode = 0;
              }
              SCORE.assignScore(data);
            },
          },
        ],
        // Phase is only executed when the user did not choose a lane in phase 1.1
        conditional_function: () => newGame.followTruckSignal === 0,
      },
    ],
    conditional_function: () => {
      // truckPhase is only executed when BOTH:
      // 1) occurrenceTruck flag is 1
      // 2) User cannot avoid the truck (avoidTruck == 0) OR user has the ability but fails to do it (preventTruck == 0)
      return (
        newGame.occurrenceTruck !== 0 && (newGame.avoidTruck === 0 || newGame.preventTruck === 0)
      );
    },
  };

  const phase2and3 = {
    timeline: [
      // Phase 2: Start of CSI period, triggered by proximity zone initiation in previous phase
      // Terminates when CSI is over and it's time to show the license
      {
        // TODO: This is the exact same as mission 1 -> separate function?
        choices: "NO_KEYS",
        trial_duration: newGame.csi,
        on_load: () => newGame.startPhase2(),
        on_finish: (data) => {
          data.name = "licensePlateOn";
          data.timestamp = jsPsych.data.get().last(2).values()[0].time_elapsed;
          data.driverSpeed = newGame.driverCarSpeed;
          data.distanceToTarget = jsPsych.data.get().last(2).values()[0].distanceToTarget;
          SCORE.assignScore(data);
        },
      },

      // Phase 3: At the start of this phase, the license is displayed
      // Relies on the user to press one of the 6 correct key lanes to trigger the end of Phase 3
      {
        // TODO: This is the exact same as mission 1 -> separate function?
        choices: "NO_KEYS", // NOTE: Keyboard events are handled inside Game
        on_load: () => newGame.startPhase3(),
        on_finish: (data) => {
          data.name = "responseKeyPressed";
          data.response_keypress_time = data.time_elapsed;
          data.keyCode = KEY_CODES[data.response_event_code ?? data.response] ?? null; // keyCode is null if no response is given
          data.responseTime =
            data.time_elapsed - jsPsych.data.get().last(2).values()[0].time_elapsed;
          data.driverSpeed = newGame.driverCarSpeed;
          // displayLicense()/captureLicensePlateOnTime() only run once startPhase3's on_load
          // fires, which is after the "licensePlateOn" (phase 2) row already saved - so this
          // is the earliest row where the value is actually available
          data.license_plate_on_time = newGame.licensePlateOnTime;

          SCORE.assignScore(data);
        },
      },
    ],
    // Phases 2 and 3 is executed when EITHER:
    // 1) The user choose a lane in phase 1.1
    // 2) The user did nopt choose a lane in phase 1.1 and the truck does not move
    conditional_function: () => {
      // TODO: This can be return !(...)
      if (
        newGame.followTruckSignal === 1 ||
        (newGame.followTruckSignal === 0 && newGame.truckMoves === 0)
      ) {
        return false;
      }
      return true;
    },
  };

  /** Build and return the trial */

  return {
    type: CustomHtmlKeyboardResponsePlugin,
    stimulus,
    data: { trialConfig },
    timeline: [
      //Phase 0: Board initializes, end of phase 0 is when target car appears
      {
        // TODO: This is the same as mission 1 -> separate function
        choices: "NO_KEYS",
        trial_duration: newGame.targetLaunchDelay,
        on_load: () => {
          newGame.startPhase0();
        },
        on_finish: (data) => {
          data.name = "initializeTarget";
          data.driverSpeed = newGame.driverCarSpeed;
          data.distanceToTarget = newGame.driverDistanceToTarget;
          data.trialStart = {
            name: "trialStart",
            driverSpeed: newGame.driverCarSpeed,
            timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
          };
          SCORE.assignScore(data);
        },
      },

      // Phase 1: Target car begins traveling towards driver car.
      // Optionally, truck may appear. And optionally, user may move to speed past truck.
      // Terminates when target car/driver car lock in proximity zone
      {
        // TODO: This is the same as mission 1 -> separate function
        choices: "NO_KEYS", // NOTE: Keyboard events are handled inside Game
        on_load: () => newGame.startPhase1(),
        on_finish: (data) => {
          data.name = "engageTarget";
          data.pulseTTLtrialStart = newGame.ttlPulseFired ? 1 : 0;
          data.TTLpulse_time = newGame.ttlPulseTime;
          data.cueCarOn = {
            name: "cueCarOn",
            driverSpeed: newGame.driverCarSpeed,
            timestamp: jsPsych.data.get().last(1).values()[0].time_elapsed,
            distanceToTarget: newGame.driverDistanceToTarget,
            csi: newGame.csi,
          };
          // Crash - move on to the next timeline (e.g. skip the rest of this Game's phases)
          if (data.crashed) jsPsych.endCurrentTimeline();
          else data.crashed = false;

          SCORE.assignScore(data);
        },
      },

      //Optional phase1.5, where truck stuff will happen.
      truckPhase,

      //Optional phase2 and 3, depends on outcome of phase1.5
      phase2and3,

      // Phase 4: Once lane has been chosen in phase 3, phase 4 begins with the driver car moving to the user chosen lane
      // Terminates when the driver car reaches the chosen lane.
      {
        // TODO: This is the same as mission 1 -> separate function
        choices: "NO_KEYS",
        // Start the phase based on the lane chosen
        on_load: () => newGame.startPhase4(jsPsych.data.get().last(1).values()[0].keyCode),
        on_finish: (data) => {
          data.name = "driverInResponseLane";
          data.driverSpeed = newGame.driverCarSpeed;
          SCORE.assignScore(data);
        },
      },

      //Phase 5
      // Once driver is in chosen lane, the package will appear and travel down the correct lane. Simultaneously, the target car also moves down the highway until it disappears.
      // Once the package reaches the end of the highway, a score will show above the driver car, showing the points the driver car received in this game trial.
      {
        // TODO: This is the same as mission 1 -> separate function
        choices: "NO_KEYS",
        on_load: () => newGame.startPhase5(),
        on_finish: (data) => {
          data.name = "packageDisappears";
          data.driverSpeed = newGame.driverCarSpeed;
          data.emotion = newGame.emotion;
          data.packageInTargetLane = {
            timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
            driverSpeed: newGame.driverCarSpeed,
            packageDistance: newGame.packageDistanceToTarget,
          };
          data.targetDisappears = {
            timestamp: newGame.targetCarDisappearTimestamp,
            driverSpeed: newGame.driverCarSpeed,
          };
          data.emotionReaction = {
            emotion: newGame.emotion,
            driverSpeed: newGame.driverCarSpeed,
            timestamp:
              jsPsych.data.get().last(1).values()[0].time_elapsed - newGame.feedbackDuration,
          };

          SCORE.assignScore(data);
        },
      },

      // Phase 7: Driver car travels back to the center of the highway
      // Triggered once user completes survey
      {
        // TODO: This is the same as mission 1 -> separate function
        choices: "NO_KEYS",
        on_load: () => newGame.startPhase7(),
        on_finish: (data) => {
          data.name = "driverAtCenter";
          data.driverToCenter = {
            timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
            driverSpeed: newGame.driverCarSpeed,
          };
          data.driverSpeed = newGame.driverCarSpeed;
          SCORE.assignScore(data);
        },
      },
    ],
  };
}

/**
 * Builds a tutorial task with selectively included phases for mission two
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @param {Object} trialConfig The task configuration used to build the trial
 * @param {String} stimulus The HTML stimulus to show
 * @param {Array} phases Array of phase numbers to include (e.g., [0, 1] or [3, 4, 5])
 * @param {Object} customOptions Optional overrides: agentFile, disableAcceleration, disableDeceleration, customTrialConfigOverrides, ready/congrats overlays
 * @returns A jsPsych node with the tutorial phases
 */
function buildMissionTwoTaskWithPhases(
  jsPsych,
  metadata,
  trialConfig,
  stimulus,
  phases = [1],
  customOptions = {}
) {
  const buildersMap = {
    0: buildPhase0,
    1: buildPhase1,
    1.1: buildPhase1_1,
    1.2: buildPhase1_2,
    2: buildPhase2,
    3: buildPhase3,
    4: buildPhase4,
    5: buildPhase5,
    7: buildPhase7,
  };

  const { tutorialHeader, ...otherOptions } = customOptions;

  // Recreate stimulus with tutorialHeader if provided
  let finalStimulus = stimulus;
  if (tutorialHeader) {
    const configForStimulus = {
      ...trialConfig,
      ...(otherOptions.agentFile ? { agentFile: otherOptions.agentFile } : {}),
      tutorialHeader,
    };
    finalStimulus = createStimulus(
      MISSION_NUMBER,
      1,
      configForStimulus
    );
  }
  const overlayOptions = {
    ready: {
      forwardText: `[press "__FORWARD_KEY__" twice to start]`,
      backText: `[press "__REPLAY_KEY__" to play video]`,
      imageOrVideoUrl: "assets/images/instructions/video_placeholder.svg",
      showText: false,
    },
    congrats: {
      forwardText: `[press "__FORWARD_KEY__" to continue ->]`,
      backText: "",
      imageOrVideoUrl: "assets/images/instructions/demo_congratulations.png",
      forwardTextCrash: `[press "__FORWARD_KEY__" to try again ]`,
      backTextCrash: "",
      imageOrVideoUrlCrash: "assets/images/instructions/demo_try_again.png",
      showText: false,
    },
  };

  const mergedOverlayOptions = {
    ready: { ...overlayOptions.ready, ...otherOptions.ready },
    congrats: { ...overlayOptions.congrats, ...otherOptions.congrats },
    followUpOnly: otherOptions.followUpOnly ?? false,
  };

  const trialConfigWithOverrides = {
    ...trialConfig,
    ...(otherOptions.agentFile ? { agentFile: otherOptions.agentFile } : {}),
    ...(otherOptions.disableAcceleration !== undefined
      ? { disableAcceleration: otherOptions.disableAcceleration }
      : {}),
    ...(otherOptions.disableDeceleration !== undefined
      ? { disableDeceleration: otherOptions.disableDeceleration }
      : {}),
    ...(otherOptions.hideTargetCar !== undefined
      ? { hideTargetCar: otherOptions.hideTargetCar }
      : {}),
    ...(otherOptions.customTrialConfigOverrides || {}),
  };

  return buildTutorialWithPhasesMissionTwo({
    jsPsych,
    metadata,
    score: SCORE,
    trialConfig: trialConfigWithOverrides,
    stimulus: finalStimulus,
    phases,
    buildersMap,
    overlayOptions: mergedOverlayOptions,
    customOptions: {
      addPhase2Delay: customOptions.addPhase2Delay ?? false,
      enableTruckPhaseSkip: customOptions.enableTruckPhaseSkip ?? false,
    },
    disableAcceleration: customOptions.disableAcceleration ?? false,
    validateSlashKey: customOptions.validateSlashKey ?? false,
    hooks: {
      onBeforeCongratsOverlay: (data) => {
        if (data.tryAgain) {
          tutorialFailureCount++;
          console.log(
            `❌ Tutorial failed (attempt ${tutorialFailureCount}/${failureLimit})`
          );
          if (tutorialFailureCount >= failureLimit) {
            // Redirect to support page
            window.location.href = 'supportPage.html';
            return true; // Prevents overlay from showing
          }
        }
        return false;
      },
    },
  });
}

/**
 * Builds the trial needed for mission one's task
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @param {Object} trialConfig The task configuration used to build the trial
 * @param {String} stimulus The HTML stimulus to show
 * @param {Score} score The user's current score
 * @returns
 */
function buildMissionTwoMessage(jsPsych, metadata, trialConfig, stimulus) {
  const newGame = new Game(jsPsych, metadata, SCORE, trialConfig);
  const hqConfig = getHQMessagePanelConfig(MISSION_NUMBER, trialConfig.agentFile);
  return {
    type: CustomHtmlKeyboardResponsePlugin,
    stimulus,
    data: { trialConfig },
    choices: ["Space"],
    on_load: () => {
      newGame.startPhase6();
      if (hqConfig) injectHQMessage(jsPsych, hqConfig);
    },
    on_finish: (data) => {
      removeHQMessage();
      SCORE.assignScore(data);
    },
  };
}

/**
 * Builds the trial needed for mission one's feedback
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} trialConfig The task configuration used to build the trial
 * @param {String} missionMinScore Per mission minimum score to continue
 * @param {String} missionMaxCrash Per mission max crashes allowed
 * @returns
 */
function buildMissionTwoFeedback(jsPsych, trialConfig, missionMinScore, missionMaxCrash) {
  return {
    data: { trialConfig },
    timeline: [
      // Display the total rewards
      ...buildFeedbackTrial(
        jsPsych,
        SCORE,
        missionMinScore,
        missionMaxCrash,
        "assets/images/postgame/FeedbackScreen_Mission2.svg"
      ),
      // Display the user's achievements
      buildAchievementsTrial("assets/images/postgame/achievementScreen_mission2.svg"),
    ],
  };
}

/**
 * Builds the trial needed for mission one's task
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @param {Object} trialConfig The task configuration used to build the trial
 * @param {String} stimulus The HTML stimulus to show
 * @returns
 */
function buildMissionTwoSurvey(jsPsych, metadata, trialConfig, stimulus) {
  const newGame = new Game(jsPsych, metadata, SCORE, trialConfig);

  return {
    type: CustomHtmlKeyboardResponsePlugin,
    stimulus,
    data: { trialConfig },
    timeline: [
      //Phase 0: Board initializes, end of phase 0 is when target car appears
      {
        choices: "NO_KEYS",
        trial_duration: newGame.targetLaunchDelay,
        on_load: () => newGame.startPhase0(),
      },
      // Phase 6: Headquarter Survey
      {
        on_load: () => {
          newGame.startPhase(6);
          hideElement(newGame.targetCar);
          hideElement(newGame.agentImage);
          newGame.playEffect("message");
          injectSurveyPanel(jsPsych, {
            portrait: "hq-happy",
            promptText: "How are you?",
          });
        },
        choices: ["KeyS", "KeyD", "KeyF", "KeyK", "KeyL", "Semicolon"],
        trial_duration: newGame.headquarterMessageDuration,
        on_finish: (data) => {
          removeSurveyPanel();
          data.name = "messageFromHQ";
        },
      },
    ],
  };
}
